import { REQUIREMENT_TYPE_ENUM } from 'state/campaigns'
import { MAX_PAGE_SIZE } from 'state/review'
import { PartiposterProfile } from 'state/submissions'
import { api, PartipostApiPaginationWrapper, PartipostApiResponse } from '.'
import { PLATFORM_ENUM } from './constants'
import {
    APPROVE_ORDER_MODE,
    BLOCK_TYPE,
    MassRegistrationPayload,
    PriorityListOrderingPayload,
    UpdateRegistrationScheduleRequestPayload,
    UpdateWaitlistOrderModePayload,
    WaitlistOrderModePayload,
    HighlightRegistrationPayload,
} from 'state/registrations'
import { ApiResponse } from 'apisauce'
import qs from 'query-string'
import { ActionType } from 'components/Submissions/Review/Registrations/ListApprovalModal'
import { CProfileState } from 'state/profile'
import { SubmissionFiltersResponse } from './campaigns'

interface Image {
    public_id: string
    resource_type: string
    file_format: string
}

export interface QuestionBlockOption {
    uuid: string
    index: number
    option: string
    is_others: boolean
}

export interface RegistrationBlockData {
    number_of_photos?: number
    is_long_answer?: boolean
    question_number?: {
        min: number
        max: number
    }
}

export interface RegistrationBlock {
    uuid: string
    index: number
    type: BLOCK_TYPE
    title: string
    description: string
    is_required: boolean
    images: Image[]
    options: QuestionBlockOption[]
    data: RegistrationBlockData
}

export interface RegistrationForm {
    uuid: string
    title: string
    description: string
    time_window: {
        start: string
        end: string
    }
    blocks: RegistrationBlock[]
    images: Image[]
    approval_expiration_days: number
    max_slots: number
    waitlist_order_mode: APPROVE_ORDER_MODE
    enable_collect_follower_count?: boolean
}

export interface GetRegistrationFormRequest {
    uuid: string
}
export type GetRegistrationFormResponse = PartipostApiPaginationWrapper<RegistrationForm[]>
export const getRegistrationForm = (payload: GetRegistrationFormRequest) => {
    const { uuid } = payload
    return api.get<PartipostApiResponse<GetRegistrationFormResponse>>(`campaigns/${uuid}/registration-forms/`)
}

export interface CreateRegistrationFormRequest {
    uuid: string
    data: Omit<RegistrationForm, 'uuid'>
    onSuccessCallback?: () => void
}
export const createRegistrationForm = (payload: CreateRegistrationFormRequest) => {
    const { uuid, data } = payload
    return api.post<PartipostApiResponse<RegistrationForm>>(`campaigns/${uuid}/registration-forms/`, data)
}

export interface UpdateRegistrationFormRequest {
    uuid: string
    data: RegistrationForm
    onSuccessCallback?: () => void
}
export const updateRegistrationForm = (payload: UpdateRegistrationFormRequest) => {
    const { uuid, data } = payload
    return api.patch<PartipostApiResponse<RegistrationForm>>(`campaigns/${uuid}/registration-forms/${data.uuid}/`, data)
}

export const updateWaitlistOrderMode = (payload: UpdateWaitlistOrderModePayload) => {
    const { campaignUuid, waitlist_order_mode } = payload
    return api.patch<PartipostApiResponse<WaitlistOrderModePayload>>(
        `campaigns/${campaignUuid}/registration-forms/waitlist-order-mode/`,
        { waitlist_order_mode: waitlist_order_mode },
    )
}

export enum REGISTRATION_STATUS_ENUM {
    PENDING = 1,
    AWAITING_RESPONSE = 2,
    ACCEPTED_INVITES = 3,
    JOINED_CAMPAIGN = 4,
    DECLINED_INVITES = 5,
    WAITLISTED = 6,
}

export interface UnmetRequirement {
    id: REQUIREMENT_TYPE_ENUM
    platform?: PLATFORM_ENUM
}

export interface RegistrationResult {
    uuid: string
    submission_uuid: string
    partiposter_profile: PartiposterProfile
    created_at: string
    approved_at: string
    accepted_at: string
    declined_at: string | null
    first_content_submitted_at: string | null
    status: REGISTRATION_STATUS_ENUM
    results_by_block: any[]
    unmet_requirement_types: UnmetRequirement[]
    recommended: boolean
    is_expiration_reverted: boolean
    approval_expired_at: string
    viewed_at: null | string
    highlighted: boolean
    reviewed_by?: CProfileState
}

export interface GetRegistrationResultsRequest {
    campaignUuid: string
    status: REGISTRATION_STATUS_ENUM
    offset?: number
    keywords?: string
    is_priority?: boolean
    ordering?: string
    filteringParams?: {
        [key: string]: string[] | string | undefined
    }
}
export type RegistrationResultsResponse = PartipostApiPaginationWrapper<RegistrationResult[]>
export const getRegistrationResultsList = (payload: GetRegistrationResultsRequest) => {
    const { status, offset, campaignUuid, keywords, is_priority, ordering, filteringParams } = payload
    const parameters = {
        status: status,
        offset,
        limit: MAX_PAGE_SIZE,
        keywords,
        is_priority,
        ordering: ordering
            ? ordering
            : status === REGISTRATION_STATUS_ENUM.PENDING
              ? '-created_at'
              : status === REGISTRATION_STATUS_ENUM.AWAITING_RESPONSE
                ? 'cm_user_approval_at'
                : status === REGISTRATION_STATUS_ENUM.ACCEPTED_INVITES
                  ? 'user_approval_at'
                  : status === REGISTRATION_STATUS_ENUM.DECLINED_INVITES
                    ? 'user_approval_at'
                    : null,
        ...filteringParams,
    }
    return api.get<PartipostApiResponse<RegistrationResultsResponse>>(
        `campaigns/${campaignUuid}/registration-results/`,
        parameters,
        {
            paramsSerializer: function (params: object) {
                // only use for parsing `location` parameters now, e.g.:
                // params: { location: ['313', '320'] }
                // return value: '?location=313&location=320'
                //
                // for other parameters like `questions`
                // the multiple parameters are parsed to string already at this moment, e.g.:
                // params: { questions: 'uuid1_value1,uuid2_contains_value2' }
                return qs.stringify(params, { arrayFormat: 'none' })
            },
        },
    )
}

export const getRegistrationResultFilters = (payload: GetRegistrationResultsRequest) => {
    return api.get<PartipostApiResponse<SubmissionFiltersResponse>>(
        `campaigns/${payload.campaignUuid}/registration-results/filters/`,
        { status: payload.status },
    )
}

export interface RequestExportRegistrationResultsPayload {
    uuid: string
    status: REGISTRATION_STATUS_ENUM
}
export const exportRegistrationResults = (payload: RequestExportRegistrationResultsPayload) => {
    const { uuid, status } = payload
    const parameters = {
        status: status,
    }
    return api.get<PartipostApiResponse>(`campaigns/${uuid}/registration-results/export/`, parameters)
}

export interface MassRegistrationResponse extends MassRegistrationPayload {
    data?: any
}

export const massRegistration = (
    payload: MassRegistrationPayload,
): Promise<ApiResponse<PartipostApiResponse<MassRegistrationResponse>>> => {
    const { campaignUuid, uuids, action_name } = payload

    const requestBody = {
        uuids,
        action_name,
    }
    return api.post<PartipostApiResponse<MassRegistrationResponse>>(
        `campaigns/${campaignUuid}/registration-results/mass-action/`,
        requestBody,
    )
}

export interface MassApproveByUsernameRequest {
    campaignUuid: string
    platform: PLATFORM_ENUM
    usernames: string[]
    actionType: ActionType
    callback: (step: number) => void
}
export interface MassApproveByUsernameError {
    username: string
    error: string
}

export interface MassApproveRegistrationByUsernameResponse {
    success_count: number
    errors: MassApproveByUsernameError[]
}

export const massApproveByUsername = (payload: MassApproveByUsernameRequest): Promise<any> => {
    const { campaignUuid, usernames, actionType, platform } = payload

    return api.post<PartipostApiResponse<MassApproveRegistrationByUsernameResponse>>(
        `campaigns/${campaignUuid}/registration-results/${
            ActionType.WAITLIST === actionType
                ? 'mass-waitlist-by-platform-username'
                : 'mass-approve-by-platform-username'
        }/`,
        { usernames, platform },
    )
}

export const savePriorityListOrdering = (payload: PriorityListOrderingPayload): Promise<any> => {
    const { campaignUuid, sortingPriorityList } = payload

    return api.post<PartipostApiResponse<MassApproveRegistrationByUsernameResponse>>(
        `campaigns/${campaignUuid}/registration-results/priority-waitlisted-sorting/`,
        sortingPriorityList,
    )
}

export const updateSchedule = (payload: UpdateRegistrationScheduleRequestPayload) => {
    const { uuid, time_window, approval_expiration_days } = payload
    return api.patch<PartipostApiResponse<RegistrationForm>>(`registration-forms/${uuid}/schedules/`, {
        time_window,
        approval_expiration_days,
    })
}

export interface UpdateRegistrationMarkAsViewed {
    uuids: string[]
    campaignUuid: string
}

export const updateMarkAsViewed = (payload: UpdateRegistrationMarkAsViewed) => {
    const { uuids, campaignUuid } = payload
    return api.patch<PartipostApiResponse<any>>(`campaigns/${campaignUuid}/registration-results/mark-as-viewed/`, {
        registration_result_uuids: uuids,
    })
}

export const highlightRegistration = (payload: HighlightRegistrationPayload) => {
    const { campaignUuid, registrationUuid, highlighted } = payload
    return api.patch<PartipostApiResponse<any>>(`campaigns/${campaignUuid}/registration-results/highlight/`, [
        {
            registration_result_uuid: registrationUuid,
            highlighted: highlighted,
        },
    ])
}
